import React, { useCallback, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Spin } from "antd";
import { retrieveCustomerPrices } from "utils/api";
import { isEmpty } from "lodash";
import classnames from "classnames";
import { customDateFormatWithoutTime, statusFormatterWithMap, textFormatter } from "components/Common/formatter";
import { STATUS_COLOR_MAP } from "pages/price-management/ConsumptionBands/variables";
import { PRICING_OPTIONS_MAP } from "pages/price-management/Prices/variables";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
  },
  priceSection: {
    marginBottom: 30,

    "&>span": {
      position: "relative",
      bottom: 10,
      color: "#00000080",
      fontWeight: 400,
    },

    "@media (max-width: 576px)": {
      margin: 0,
      padding: 20,
      background: "white",
      borderRadius: 20,
    },
  },
  noData: {
    color: "#444",
    fontWeight: 300,
    backgroundColor: "#0000000F",
  },
  priceContainer: {
    display: "flex",
    justifyContent: "space-between",
    padding: 20,
    border: "1px solid #0000000F",
    borderRadius: 5,

    "@media (max-width: 576px)": {
      flexDirection: "column",
      padding: 0,
      border: "none",
    },
  },
  nameContainer: {
    display: "flex",
    fontWeight: 600,

    "&>div": {
      marginLeft: 10,
    },
  },
  valueContainer: {
    fontWeight: 300,
    color: "#444444",
    marginLeft: 30,
    textTransform: "uppercase",
    display: "flex",

    "&>span": {
      left: 5,
      position: "relative",
      fontWeight: 600,
      maxWidth: 190,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },

    "@media (max-width: 576px)": {
      margin: 0,
      textTransform: "none",
      justifyContent: "space-between",
      padding: "5px 0",
    },
  },
  dataContainer: {
    display: "flex",

    "@media (max-width: 576px)": {
      flexDirection: "column",
    },
  },
});

const UnitPriceTab = ({ customer }) => {
  const classes = useStyles();
  const [isLoading, setLoader] = useState(false);
  const [data, setData] = useState({
    depot: {},
    consumption: {},
    customer: {},
    default: {},
  });

  const getData = useCallback(() => {
    setLoader(true);
    retrieveCustomerPrices(customer.id)
      .then(res => {
        const {
          result: { items },
        } = res?.data;
        const depot = items.find(item => item.type === "Depot") || {};
        const consumption = items.find(item => item.type === "Consumption") || {};
        const customer = items.find(item => item.type === "CustomerGroup") || {};
        const defaultPrice = items.find(item => item.type === "Default") || {};
        setLoader(false);
        setData({
          depot,
          consumption,
          customer,
          default: defaultPrice,
        });
      })
      .catch(() => {
        setLoader(false);
      });
  }, [customer]);

  useEffect(() => {
    getData();
  }, []);

  return (
    <div className={classes.root}>
      <Spin spinning={isLoading}>
        {!isEmpty(data.default) && (
          <div className={classes.priceSection}>
            <span>DEFAULT PRICE</span>
            <div className={classnames(classes.priceContainer, isEmpty(data.default) && classes.noData)}>
              <>
                <div className={classes.nameContainer}>
                  {data.default.name}
                  <div>{statusFormatterWithMap(data.default?.status, STATUS_COLOR_MAP)}</div>
                </div>
                <div className={classes.dataContainer}>
                  <div className={classes.valueContainer} title={data.default.customerGroup}>
                    Product
                    <span>{data.default.product}</span>
                  </div>
                  <div className={classes.valueContainer}>
                    Final Updated Price
                    <span>{data.default.actualAmount}</span>
                  </div>
                  <div
                    className={classes.valueContainer}
                    title={`${customDateFormatWithoutTime(data.default.activationDate)} / ${
                      data.default.deactivationDate ? customDateFormatWithoutTime(data.default.deactivationDate) : "-"
                    }`}
                  >
                    Validity
                    <span>{`${customDateFormatWithoutTime(data.default.activationDate)} / ${
                      data.default.deactivationDate ? customDateFormatWithoutTime(data.default.deactivationDate) : "-"
                    }`}</span>
                  </div>
                </div>
              </>
            </div>
          </div>
        )}
        {!isEmpty(data.depot) && (
          <div className={classes.priceSection}>
            <span>DEPOT BASED SEGMENTATION</span>
            <div className={classnames(classes.priceContainer, isEmpty(data.depot) && classes.noData)}>
              <>
                <div className={classes.nameContainer}>
                  {data.depot.name}
                  <div>{statusFormatterWithMap(data.depot?.status, STATUS_COLOR_MAP)}</div>
                </div>
                <div className={classes.dataContainer}>
                  <div className={classes.valueContainer}>
                    LPG Price (KG)
                    <span>{data.depot.actualAmount}</span>
                  </div>
                  <div
                    className={classes.valueContainer}
                    title={`${customDateFormatWithoutTime(data.depot.activationDate)} / ${
                      data.depot.deactivationDate ? customDateFormatWithoutTime(data.depot.deactivationDate) : "-"
                    }`}
                  >
                    Validity
                    <span>{`${customDateFormatWithoutTime(data.depot.activationDate)} / ${
                      data.depot.deactivationDate ? customDateFormatWithoutTime(data.depot.deactivationDate) : "-"
                    }`}</span>
                  </div>
                </div>
              </>
            </div>
          </div>
        )}
        {!isEmpty(data.consumption) && (
          <div className={classes.priceSection}>
            <span>CONSUMPTION SEGMENTATION</span>
            <div className={classnames(classes.priceContainer, isEmpty(data.consumption) && classes.noData)}>
              <>
                <div className={classes.nameContainer}>
                  {data.consumption.name}
                  <div>{statusFormatterWithMap(data.consumption?.status, STATUS_COLOR_MAP)}</div>
                </div>
                <div className={classes.dataContainer}>
                  <div className={classes.valueContainer} title={data.consumption.consumptionBand}>
                    Consumption Band
                    <span>{data.consumption.consumptionBand}</span>
                  </div>
                  <div className={classes.valueContainer}>
                    {PRICING_OPTIONS_MAP[data.consumption.pricingOption]}
                    <span>{data.consumption.amount}</span>
                  </div>
                  <div className={classes.valueContainer}>
                    LPG Price (KG)
                    <span>{data.consumption.actualAmount}</span>
                  </div>
                  <div
                    className={classes.valueContainer}
                    title={`${customDateFormatWithoutTime(data.consumption.activationDate)} / ${
                      data.consumption.deactivationDate
                        ? customDateFormatWithoutTime(data.consumption.deactivationDate)
                        : "-"
                    }`}
                  >
                    Validity
                    <span>{`${customDateFormatWithoutTime(data.consumption.activationDate)} / ${
                      data.consumption.deactivationDate
                        ? customDateFormatWithoutTime(data.consumption.deactivationDate)
                        : "-"
                    }`}</span>
                  </div>
                </div>
              </>
            </div>
          </div>
        )}
        {!isEmpty(data.customer) && (
          <div className={classes.priceSection}>
            <span>CUSTOM GROUP</span>
            <div className={classnames(classes.priceContainer, isEmpty(data.customer) && classes.noData)}>
              <>
                <div className={classes.nameContainer}>
                  {data.customer.name}
                  <div>{statusFormatterWithMap(data.customer?.status, STATUS_COLOR_MAP)}</div>
                </div>
                <div className={classes.dataContainer}>
                  <div className={classes.valueContainer} title={data?.customer?.customerGroupName}>
                    Customer Group
                    <span>{textFormatter(data?.customer?.customerGroupName)}</span>
                  </div>
                  <div className={classes.valueContainer}>
                    LPG Price (KG)
                    <span>{data.customer.actualAmount}</span>
                  </div>
                  <div
                    className={classes.valueContainer}
                    title={`${customDateFormatWithoutTime(data.customer.activationDate)} / ${
                      data.customer.deactivationDate ? customDateFormatWithoutTime(data.customer.deactivationDate) : "-"
                    }`}
                  >
                    Validity
                    <span>{`${customDateFormatWithoutTime(data.customer.activationDate)} / ${
                      data.customer.deactivationDate ? customDateFormatWithoutTime(data.customer.deactivationDate) : "-"
                    }`}</span>
                  </div>
                </div>
              </>
            </div>
          </div>
        )}
      </Spin>
    </div>
  );
};

export default UnitPriceTab;
